import { computed } from 'vue'
import { I18nService } from '@/domain/services/i18n.service'
import { I18nRepository } from '@/infrastructure/repositories/i18n.repository'

/**
 * Композабл для роботи з локаллю в роуті: валідація, резолв з fallback, оновлення сегмента.
 * Не залежить від useLocaleSwitch — зв'язку з i18n робить викликаючий код.
 */
export function useRouteLocale() {
  const route = useRoute()
  const router = useRouter()

  const i18nService = I18nService.getInstance(I18nRepository.getInstance())
  const supportedLocales = i18nService.getSupportedLocales()
  const defaultLocale = i18nService.getDefaultLocale()

  /** Сирий параметр locale з роута (може бути невалідним) */
  const currentRouteLocale = computed(() => route.params.locale as string | undefined)

  /** Чи параметр locale у роуті є підтриманою локаллю (будь-якою з списку I18n) */
  const isRouteLocaleValid = computed(() => {
    const loc = (route.params.locale as string) ?? ''
    return loc !== '' && supportedLocales.includes(loc)
  })

  /** Поточна локаль для UI та API: з роута або fallback на defaultLocale з I18nService */
  const currentLocale = computed<string>(() => {
    const loc = route.params.locale as string
    return loc && supportedLocales.includes(loc) ? loc : defaultLocale
  })

  /**
   * Оновлює сегмент локалі в поточному шляху та виконує навігацію.
   * Не змінює i18n — після навігації useLocaleSwitch по watcher виставить локаль.
   */
  const updateRouteLocale = async (newLocale: string): Promise<void> => {
    let newPath: string

    if (route.params.locale) {
      const current = route.params.locale as string
      newPath = route.path.replace(`/${current}`, `/${newLocale}`)
    } else {
      // Current route has no locale segment (e.g. /my/progress) — don't navigate away
      return
    }

    if (newPath !== route.path) {
      try {
        await router.push(newPath)
      } catch (error) {
        const errorMessage = `Could not navigate to ${newPath}, route may not exist`
        console.warn(errorMessage)
        throw new Error(errorMessage)
      }
    }
  }

  return {
    currentRouteLocale,
    isRouteLocaleValid,
    currentLocale,
    updateRouteLocale,
  }
}
